import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import seoMeta, { ORIGIN } from '../data/seoMeta';
import { appsData } from '../data/appsData';
import { getAppMeta } from '../data/appMeta';

const DEFAULT_OG = `${ORIGIN}/og-image.png`;

// Real App Store links only (skip id000000000 / id0000000000 placeholders and null).
const isRealAppStoreUrl = (u) =>
  typeof u === 'string' && u.includes('apps.apple.com') && !/id0{8,}/.test(u);

const absolute = (path) => {
  if (!path) return DEFAULT_OG;
  if (/^https?:\/\//.test(path)) return path;
  return ORIGIN + (path.startsWith('/') ? path : `/${path}`);
};

function upsertMeta(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
}

function upsertCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function setJsonLd(blocks) {
  let el = document.getElementById('seo-jsonld');
  if (!blocks.length) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = 'seo-jsonld';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(blocks.length === 1 ? blocks[0] : blocks);
}

function buildJsonLd(route, meta) {
  const blocks = [];
  const app = appsData.find((a) => a.customUrl === route);

  if (route === '/apps' || app) {
    const crumbs = [
      { '@type': 'ListItem', position: 1, name: 'Home', item: ORIGIN + '/' },
      { '@type': 'ListItem', position: 2, name: 'Apps', item: ORIGIN + '/apps' },
    ];
    if (app) crumbs.push({ '@type': 'ListItem', position: 3, name: app.name, item: ORIGIN + route });
    blocks.push({ '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: crumbs });
  }

  if (app && meta.category) {
    const m = getAppMeta(app);
    const sa = {
      '@context': 'https://schema.org',
      '@type': 'MobileApplication',
      name: app.name,
      operatingSystem: app.playStoreUrl ? 'iOS, Android' : 'iOS',
      applicationCategory: meta.category,
      url: ORIGIN + route,
      description: meta.description,
      image: absolute(m.icon),
      author: { '@type': 'Organization', name: 'Panda Taps LLC', url: ORIGIN },
    };
    // Only assert a download link + offer when the app is actually live.
    if (isRealAppStoreUrl(app.appStoreUrl)) {
      sa.installUrl = app.appStoreUrl;
      sa.downloadUrl = app.appStoreUrl;
      sa.offers = { '@type': 'Offer', price: '0', priceCurrency: 'USD', category: 'free' };
    }
    blocks.push(sa);
  }
  return blocks;
}

export default function Seo() {
  const { pathname } = useLocation();

  useEffect(() => {
    const url = ORIGIN + (pathname === '/' ? '' : pathname);

    // Canonical and og:url must be corrected for EVERY route, including the
    // ones with no seoMeta entry (per-app /privacy and /terms, /app/:id). Those
    // routes are served build/index.html by the SPA fallback, whose baked-in
    // canonical points at the homepage - so without this they each told Google
    // "I am a duplicate of pandataps.com" and dropped out of the index. This
    // also clears the stale canonical left behind by client-side navigation.
    upsertCanonical(url);
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: url });

    const meta = seoMeta[pathname];
    if (!meta) return; // dynamic/legal routes manage their own <title>
    const ogImage = meta.image ? ORIGIN + meta.image : DEFAULT_OG;

    document.title = meta.title;
    upsertMeta('meta[name="description"]', { name: 'description', content: meta.description });
    upsertCanonical(url);

    upsertMeta('meta[property="og:type"]', { property: 'og:type', content: 'website' });
    upsertMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: 'Panda Taps' });
    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: meta.title });
    upsertMeta('meta[property="og:description"]', { property: 'og:description', content: meta.description });
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: url });
    upsertMeta('meta[property="og:image"]', { property: 'og:image', content: ogImage });
    upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: meta.title });
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: meta.description });
    upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: ogImage });

    setJsonLd(buildJsonLd(pathname, meta));
  }, [pathname]);

  return null;
}
